import { useEffect, useState } from "react";
import Modal from "../../../components/designs/modal";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import axiosService from "../../../core/services/axios-service";
import ToastService from "../../../core/services/toaster-service";

export default function AssignCoursesModal({ isOpen, user, onClose, onSuccess }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedCourseIds([]);
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const res = await axiosService.get(ApiRoutes.GET_COURSES);
      const allCourses = res?.data?.data || [];
      const roleTypeMap = {
        coach: "coach",
        "team_member": "team_member",
      };
      const roleType = roleTypeMap[user?.role];
      const filtered = roleType
        ? allCourses.filter((c) => c.type === roleType)
        : allCourses;
      setCourses(filtered);
    } catch (err) {
      ToastService.error("Failed to load courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  const toggleCourse = (courseId) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const handleSubmit = async () => {
    if (selectedCourseIds.length === 0) {
      ToastService.error("Please select at least one course");
      return;
    }
    setSubmitting(true);
    try {
      await axiosService.post(ApiRoutes.ASSIGN_COURSES, {
        courseIds: selectedCourseIds,
        users: [{ userId: user._id }],
      });
      ToastService.success("Courses assigned successfully");
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      ToastService.error("Failed to assign courses");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={`Assign Courses — ${user?.fullName || ""}`}
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmLabel={submitting ? "Assigning..." : "Assign"}
      confirmDisabled={submitting || loadingCourses}
    >
      {loadingCourses ? (
        <p className="assign-courses-hint">Loading courses...</p>
      ) : courses.length === 0 ? (
        <p className="assign-courses-hint">No courses available for this role.</p>
      ) : (
        <div>
          <p className="assign-courses-hint mb-3">
            Select one or more courses to assign to <strong>{user?.fullName}</strong>.
          </p>
          <div className="assign-courses-list">
            {courses.map((course) => (
              <label
                key={course._id}
                className={`assign-courses-item${selectedCourseIds.includes(course._id) ? " assign-courses-item--selected" : ""}`}
              >
                <input
                  type="checkbox"
                  className="assign-courses-checkbox"
                  checked={selectedCourseIds.includes(course._id)}
                  onChange={() => toggleCourse(course._id)}
                />
                <div>
                  <div className="assign-courses-name">{course.courseName}</div>
                  <div className="assign-courses-description">{course.description}</div>
                  <span
                    className={`assign-courses-badge assign-courses-badge--${course.type === "coach" ? "coach" : "team"}`}
                  >
                    {course.type === "team_member" ? "Team Member" : "Coach"}
                  </span>
                </div>
              </label>
            ))}
          </div>
          {selectedCourseIds.length > 0 && (
            <p className="assign-courses-count mt-2">
              {selectedCourseIds.length} course{selectedCourseIds.length > 1 ? "s" : ""} selected
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
